import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/services/firebase';
import useAuthStore from '@/stores/authStore';
import { firebaseApiClient } from '@/services/firebaseApi';

export function FirebaseAuthProvider({ children }: { children: React.ReactNode }) {
  const { user, organization, token, isAuthenticated, isLoading } = useAuthStore();
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && !isAuthenticated) {
        // User is signed in - load their profile data
        try {
          const userData = await firebaseApiClient.getCurrentUser();
          const authToken = await firebaseUser.getIdToken();
          
          // Store token for API calls
          localStorage.setItem('auth_token', authToken);
          
          // Update Zustand store directly
          useAuthStore.setState({
            user: userData.user,
            organization: userData.organization,
            token: authToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Failed to load user profile:', error);
          // Clear auth state on error
          localStorage.removeItem('auth_token');
          useAuthStore.setState({
            user: null,
            organization: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      } else if (!firebaseUser && isAuthenticated) {
        // User is signed out - clear auth state
        localStorage.removeItem('auth_token');
        useAuthStore.setState({
          user: null,
          organization: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
      
      if (initializing) {
        setInitializing(false);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, initializing]);

  // Show loading screen while initializing Firebase Auth
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return <>{children}</>;
}